import { createContext, useState, type ReactNode } from "react"
import { NavigationOption } from "../hooks/useNhsAppJs"

type BackButtonContextProps = {
  onBackPress?: NavigationOption | null
  setOnBackPress?: (appPage: NavigationOption | null) => void
  showBackButton: boolean
  setShowBackButton?: (show: boolean) => void
}

const INITIAL_CONTEXT: BackButtonContextProps = {} as BackButtonContextProps

/**
 * Context to manage back button press functionality between our app and the NHS mobile app.
 * Provides `onBackPress` callback and `setOnBackPress` function to set the callback.
 */
//eslint-disable-next-line react-refresh/only-export-components
export const BackButtonContext = createContext<BackButtonContextProps>(INITIAL_CONTEXT)

/**
 * Provider component for BackButtonContext.
 * Manages state for `onBackPress` and provides a function to set this state.
 *
 * @param {Object} props - The props object.
 * @param {ReactNode} props.children - The child components.
 * @returns {JSX.Element} The provider component wrapping its children with `BackButtonContext`.
 */
export const BackButtonProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [onBackPress, setOnBackPress] = useState<NavigationOption | null>(null)
  const [showBackButton, setShowBackButton] = useState(false)

  return (
    <BackButtonContext.Provider
      value={{
        onBackPress,
        setOnBackPress,
        showBackButton,
        setShowBackButton,
      }}
    >
      {children}
    </BackButtonContext.Provider>
  )
}
